// Smart Parse event side drawer — always v3 (`layerd.waf.event.v3`).
// Single shell for loading + content so the panel does not remount after fetch.

import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ShieldPlus } from "lucide-react";
import copy from "copy-to-clipboard";

import useEvent from "../../../../hooks/user/useEvent";
import useSite from "../../../../hooks/user/useSite";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import usePermissions from "../../../../hooks/usePermissions";
import colors from "../../../../utils/colors";
import { isV3Envelope } from "../../../../utils/wafEventV3";
import { getWafEventDetailFilter } from "./wafEventListUtils";
import {
  buildSimilarWafEventNavigationState,
  WAF_EVENTS_PATH,
} from "./wafEventNavigation";
import {
  buildFirewallBlockIpState,
  FIREWALL_RULES_PATH,
} from "../services/firewall/firewallNavigation";
import SmartParseEventDrawerV3 from "./v3/SmartParseEventDrawerV3";
import SmartParseDrawerHeader from "./v3/SmartParseDrawerHeader";
import SmartParseDrawerHero from "./v3/SmartParseDrawerHero";
import WafEventDrawerShell from "./WafEventDrawerShell";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import {
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  secondaryButtonStyle,
  primaryButtonStyle,
  formatDrawerFooterLabel,
} from "./wafEventDrawerShared";
import { spLh } from "./v3/smartParseDrawerUi";

function eventKeyFromRow(event) {
  return event?.event_id || event?.request_id || event?.id;
}

function resolveSmartParseV3Event(listEvent, detailEvent) {
  const listKey = eventKeyFromRow(listEvent);

  if (detailEvent && isV3Envelope(detailEvent)) {
    const detailKey = eventKeyFromRow(detailEvent);
    if (!listKey || !detailKey || detailKey === listKey) return detailEvent;
  }

  if (isV3Envelope(listEvent) && listEvent.story) return listEvent;
  return null;
}

function DrawerFooterContext({ site, host }) {
  const siteLabel = site?.name || site?.site_name || site?.site_id;
  const footerLabel = formatDrawerFooterLabel(siteLabel, host);
  if (!footerLabel) return null;

  const isActive = site?.status === "Active" || site?.enabled === true;

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      {isActive && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: colors.green[200],
            flexShrink: 0,
          }}
        />
      )}
      <Typography
        sx={{
          fontSize: 13,
          lineHeight: spLh.px21,
          color: TEXT_SECONDARY,
          wordBreak: "break-all",
        }}
      >
        {footerLabel}
      </Typography>
    </Stack>
  );
}

export default function SmartParseEventDrawer({ open, onClose, event }) {
  const navigate = useNavigate();
  const { getSmartParseEvent, smartParseEvent, smartParseEventLoading } = useEvent();
  const { siteList } = useSite();
  const { effectiveSiteId: siteID } = useGlobalSite();
  const { getPermissionsForSite, PERMISSIONS } = usePermissions();
  const [snackOpen, setSnackOpen] = useState(false);

  const listKey = eventKeyFromRow(event);

  useEffect(() => {
    if (!open || !event || !listKey) return;
    getSmartParseEvent(listKey, getWafEventDetailFilter(event));
  }, [open, event, listKey, getSmartParseEvent]);

  const resolvedEvent = useMemo(
    () => resolveSmartParseV3Event(event, smartParseEvent),
    [event, smartParseEvent],
  );

  const loading = smartParseEventLoading && !resolvedEvent;
  const error = !smartParseEventLoading && !resolvedEvent
    ? "Unable to load Smart Parse event details. The server did not return a v3 event envelope."
    : null;

  const selectedSite = useMemo(
    () => siteList?.find((s) => s.site_id === siteID) ?? null,
    [siteList, siteID],
  );

  const canWriteFirewall = useMemo(() => {
    if (!selectedSite) return false;
    return getPermissionsForSite(selectedSite).hasPermission(PERMISSIONS.FIREWALL_WRITE);
  }, [selectedSite, getPermissionsForSite, PERMISSIONS]);

  const sourceIp = resolvedEvent?.attacker?.source_ip
    || resolvedEvent?.attacker?.ip
    || event?.attacker?.source_ip
    || event?.attacker?.ip
    || event?.ip;
  const host = resolvedEvent?.target?.host;

  const handleCopy = (value) => {
    if (!value) return;
    copy(String(value));
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  const handleFilterSimilar = (filterType) => {
    const source = resolvedEvent || event;
    const navState = buildSimilarWafEventNavigationState(source, filterType);
    if (!navState) return;
    onClose?.();
    navigate(WAF_EVENTS_PATH, { state: navState });
  };

  const handleAddToBlockList = () => {
    const siteLabel = selectedSite?.name || selectedSite?.site_name || selectedSite?.site_id;
    const navState = buildFirewallBlockIpState(sourceIp, siteLabel);
    if (!navState) return;
    onClose?.();
    navigate(FIREWALL_RULES_PATH, { state: navState });
  };

  if (!open || !event) return null;

  const method = resolvedEvent?.target?.method || event.target?.method || event.method;
  const path = resolvedEvent?.target?.uri || resolvedEvent?.target?.path
    || event.target?.uri || event.target?.path || event.uri;

  const footerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button
        onClick={onClose}
        startIcon={<LayerdIcon icon={DETECTION_ICONS.close} size={16} color="neutral" />}
        sx={secondaryButtonStyle}
      >
        Close
      </Button>
      {canWriteFirewall && sourceIp && (
        <Button
          onClick={handleAddToBlockList}
          startIcon={<ShieldPlus size={16} />}
          sx={{
            ...primaryButtonStyle,
            boxShadow: "0 4px 14px rgba(99,113,247,0.30)",
          }}
        >
          Add to block list
        </Button>
      )}
    </Stack>
  );

  return (
    <>
      <WafEventDrawerShell
        open={open}
        onClose={onClose}
        title="Smart Parse Event"
        loading={loading}
        error={error}
        headerVariant="hero"
        headerContent={(
          <SmartParseDrawerHeader
            embedded
            eventId={listKey}
            method={method}
            path={path}
            onCopyEventId={handleCopy}
          />
        )}
        heroContent={resolvedEvent ? <SmartParseDrawerHero event={resolvedEvent} /> : null}
        footerLeft={<DrawerFooterContext site={selectedSite} host={host} />}
        footerActions={footerActions}
      >
        <SmartParseEventDrawerV3
          event={resolvedEvent}
          onCopy={handleCopy}
          onFilterSimilar={handleFilterSimilar}
        />
      </WafEventDrawerShell>

      {snackOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            backgroundColor: TEXT_PRIMARY,
            color: "#fff",
            px: 2,
            py: 1,
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1400,
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#fff" }}>
            Copied to clipboard!
          </Typography>
        </Box>
      )}
    </>
  );
}
